import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogContent,
  Divider,
  Zoom,
  Fade,
  CircularProgress,
  Alert,
  Snackbar,
  Chip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import { useContent } from '../contexts/ContentContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ContentSection, NewsArticle, Meeting } from '../services/content';
import DocumentReleaseGate from './DocumentReleaseGate';
import LoginForm from './LoginForm';

// Enhanced VideoBackground component with video and image support
const VideoBackground = ({ hero }: { hero: any }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...(hero.backgroundImage && !hero.backgroundVideo && {
        backgroundImage: `url(${hero.backgroundImage.src})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#2c3e50', // Fallback color
      }),
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1,
      },
    }}
  >
    {hero.backgroundVideo && (
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        onError={(e) => {
          console.error('Video failed to load:', e);
          console.error('Video src:', hero.backgroundVideo.src);
        }}
        onLoadStart={() => console.log('Video loading started')}
        onCanPlay={() => console.log('Video can play')}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      >
        <source src={hero.backgroundVideo.src} type={hero.backgroundVideo.type} />
        Your browser does not support the video tag.
      </video>
    )}
  </Box>
);

// Enhanced SectionTitle with animation
const SectionTitle: React.FC<{children: React.ReactNode, align?: "left" | "center" | "right", color?: string}> = ({children, align = "left", color}) => (
  <Typography 
    variant="h4" 
    component="h2" 
    sx={{ 
      fontWeight: 600, 
      mb: 3, 
      color: color || 'primary.main',
      position: 'relative',
      textAlign: align,
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -8,
        left: align === "center" ? '50%' : 0,
        transform: align === "center" ? 'translateX(-50%)' : 'none',
        width: 60,
        height: 3,
        backgroundColor: 'secondary.main',
        transition: 'width 0.3s ease-in-out',
      },
    }}
  >
    {children}
  </Typography>
);

// Dynamic icon component
const DynamicIcon: React.FC<{ iconName: string; sx?: any }> = ({ iconName, sx }) => {
  const IconComponent = (Icons as any)[iconName] || Icons.Info;
  return <IconComponent sx={sx} />;
};

// Meetings table component
const MeetingsTable: React.FC<{ meetings: Meeting[] }> = ({ meetings }) => {
  const { locale, t } = useLanguage();
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
  
  // Sort meetings by date
  const sortedMeetings = [...meetings].sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  
  const formatDate = (dateString: string) => {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  const isPastDate = (dateString: string) => {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-').map(Number);
    const meetingDate = new Date(year, month - 1, day); // month is 0-indexed
    meetingDate.setHours(0, 0, 0, 0);
    return meetingDate < today;
  };
  
  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t.dynamicAbout.meetingsTitle}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 1.5
      }}>
        {sortedMeetings.map((meeting, index) => {
          const isPast = isPastDate(meeting.date);
          return (
            <Card 
              key={index}
              sx={{ 
                opacity: isPast ? 0.6 : 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  opacity: 1,
                  boxShadow: 2,
                },
              }}
            >
              <CardContent sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                py: 2,
                '&:last-child': { pb: 2 }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 auto', flexWrap: 'wrap' }}>
                  <Box 
                    sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: isPast ? 'grey.400' : 'secondary.main',
                      flexShrink: 0
                    }} 
                  />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {meeting.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    {formatDate(meeting.date)}
                  </Typography>
                  {isPast && (
                    <Chip
                      label={t.dynamicAbout.past}
                      size="small" 
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                  {meeting.presentation && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      href={meeting.presentation}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<Icons.PictureAsPdf />}
                    >
                      {t.dynamicAbout.presentation}
                    </Button>
                  )}
                  {meeting.notes && (
                    <Button
                      size="small"
                      variant="outlined"
                      href={meeting.notes}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<Icons.Description />}
                    >
                      {t.dynamicAbout.notes}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    href={meeting.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<Icons.OpenInNew />}
                  >
                    {isPast ? t.dynamicAbout.eventInfo : t.dynamicAbout.viewDetails}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

// Content section component
const ContentSectionComponent: React.FC<{ 
  section: ContentSection; 
  index: number; 
  onComingSoon: () => void;
  onLoginClick: () => void;
}> = ({ section, index, onComingSoon, onLoginClick }) => {
  const theme = useTheme();
  
  return (
    <Fade in={true} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
      <Box sx={{ 
        py: 10, 
        backgroundColor: section.backgroundColor || '#f7f7f7',
        color: section.textColor || 'inherit',
        position: 'relative', 
        overflow: 'hidden',
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            <SectionTitle color={section.textColor}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <DynamicIcon iconName={section.icon} sx={{ mr: 1, color: 'secondary.main' }} />
                {section.title}
              </Box>
            </SectionTitle>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: section.image ? 'row' : 'column' }, 
              alignItems: 'center', 
              mb: 4, 
              gap: 4 
            }}>
              <Box sx={{ flex: '1 1 auto' }}>
                {section.content.map((paragraph, idx) => (
                  <Typography 
                    key={idx}
                    variant="body1" 
                    paragraph
                    sx={{ color: section.textColor || 'inherit' }}
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}
              </Box>
              {section.image && (
                <Box
                  component="img"
                  src={section.image.src}
                  alt={section.image.alt}
                  sx={{
                    width: { xs: '100%', md: '270px' },
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                    flex: '0 0 auto',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.03)',
                    }
                  }}
                />
              )}
            </Box>
            
            {section.meetings && section.meetings.length > 0 && (
              <MeetingsTable meetings={section.meetings} />
            )}

            {section.gatedDocuments && section.gatedDocuments.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <DocumentReleaseGate
                  documents={section.gatedDocuments}
                  sectionId={section.id}
                  onLoginClick={onLoginClick}
                />
              </Box>
            )}

            {(section.button || section.buttons) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
                {section.button && (
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    {...(section.button.link === '#coming-soon' 
                      ? { onClick: onComingSoon }
                      : { 
                          href: section.button.link, 
                          target: "_blank", 
                          rel: "noopener noreferrer" 
                        }
                    )}
                    sx={{ fontWeight: 500 }}
                  >
                    {section.button.text}
                  </Button>
                )}
                {section.buttons?.map((button, idx) => (
                  <Button 
                    key={idx}
                    variant="contained" 
                    color="primary" 
                    size="medium"
                    endIcon={<Icons.Event />}
                    {...(button.link === '#coming-soon' 
                      ? { onClick: onComingSoon }
                      : { 
                          href: button.link, 
                          target: "_blank", 
                          rel: "noopener noreferrer" 
                        }
                    )}
                    sx={{ 
                      fontWeight: 500,
                      px: 2.5,
                      py: 1,
                      boxShadow: 1,
                      '&:hover': {
                        boxShadow: 2,
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {button.text}
                  </Button>
                ))}
              </Box>
            )}

          </Box>
        </Container>
      </Box>
    </Fade>
  );
};

// News item component
const NewsItem = ({ article }: { article: NewsArticle }) => {
  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
      }
    }}>
      <CardMedia
        component="img"
        height="160"
        image={article.image}
        alt={article.title}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {article.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {article.description}
        </Typography>
        <Button 
          endIcon={<Icons.ArrowForward />} 
          href={article.link} 
          target="_blank"
          color="primary"
          sx={{ padding: 0 }}
        >
          {article.linkText}
        </Button>
      </CardContent>
    </Card>
  );
};

// Main DynamicAbout component
const DynamicAbout: React.FC = () => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  const { aboutContent, isLoading, error } = useContent();
  const { t } = useLanguage();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const handleComingSoon = () => {
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleLoginDialogClose = () => {
    setLoginDialogOpen(false);
  };
  
  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error || !aboutContent) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error || t.dynamicAbout.loadFailed}
        </Alert>
      </Container>
    );
  }
  
  return (
    <>
      {/* Hero Section */}
      <Box sx={{ position: 'relative', minHeight: '100vh', mb: 8 }}>
        <VideoBackground hero={aboutContent.hero} />
        
        {/* Hero Content - normal flow, so tall copy grows the hero instead of overflowing */}
        <Box
          data-testid="hero-content"
          sx={{
            position: 'relative',
            width: '100%',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            pt: { xs: 12, md: 14 }, // Clear the fixed transparent header
            pb: 6,
          }}
        >
          {/* Logo in flow above the title */}
          {aboutContent.logo && (
            <Box
              component="img"
              src={aboutContent.logo.src}
              alt={aboutContent.logo.alt}
              sx={{
                height: { xs: '60px', sm: '80px', md: '100px' },
                width: 'auto',
                maxWidth: '400px',
                objectFit: 'contain',
                mb: { xs: 3, md: 4 },
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' // Add shadow for visibility
              }}
            />
          )}
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ maxWidth: '800px', mx: 'auto', textAlign: 'center', color: 'white', px: 3 }}>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  fontSize: { xs: '2.2rem', md: '3.2rem' }
                }}
              >
                {aboutContent.hero.title}
              </Typography>
              <Typography 
                variant="h4" 
                component="h2" 
                sx={{ 
                  fontWeight: 500, 
                  mb: 3,
                  color: 'secondary.main',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                {aboutContent.hero.subtitle}
              </Typography>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4,
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1.2rem', md: '1.5rem' }
                }}
              >
                {aboutContent.hero.description}
              </Typography>
              
              <Box sx={{ mt: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large"
                  component={RouterLink}
                  to={aboutContent.hero.primaryButton.link}
                  sx={{ 
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    color: '#fff',
                  }}
                >
                  {aboutContent.hero.primaryButton.text}
                </Button>
                
                <Button 
                  variant="outlined" 
                  color="inherit" 
                  size="large" 
                  component="a"
                  href={aboutContent.hero.secondaryButton.link}
                  target="_blank"
                  sx={{ 
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    borderColor: 'white',
                  }}
                >
                  {aboutContent.hero.secondaryButton.text}
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Content Sections */}
      {aboutContent.sections.map((section, index) => (
        <ContentSectionComponent 
          key={section.id} 
          section={section} 
          index={index} 
          onComingSoon={handleComingSoon}
          onLoginClick={handleLoginClick}
        />
      ))}

      {/* News Section */}
      {aboutContent.news.articles.length > 0 && (
        <Fade in={true} timeout={800}>
          <Box sx={{ py: 10, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
              <SectionTitle align="center">{aboutContent.news.title}</SectionTitle>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                justifyContent: 'center', 
                gap: 4, 
                mt: 2,
                maxWidth: '100%'
              }}>
                {aboutContent.news.articles.map((article, index) => (
                  <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }} key={index}>
                    <Box sx={{ 
                      flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(33.333% - 16px)', lg: '1 1 calc(25% - 16px)' },
                      maxWidth: { xs: '100%', sm: '400px', md: '350px', lg: '300px' },
                      minWidth: { xs: '280px', sm: '280px', md: '280px', lg: '280px' }
                    }}>
                      <NewsItem article={article} />
                    </Box>
                  </Zoom>
                ))}
              </Box>
            </Container>
          </Box>
        </Fade>
      )}

      {/* Coming Soon Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={t.dynamicAbout.comingSoon}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Login Dialog for gated documents */}
      <Dialog
        open={loginDialogOpen}
        onClose={handleLoginDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <LoginForm onClose={handleLoginDialogClose} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DynamicAbout; 